import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import type { ICensusInfo } from './components/CensusLoadedRow';
import ProductRowAgeSection from './ProductRowAgeSection';
import CustomButton from '../../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import CustomCheckbox from '../../../../../../../shared/components/Inputs/CustomCheckbox/CustomCheckbox';
import CustomSelect from '../../../../../../../shared/components/Inputs/CustomSelect/CustomSelect';
import { TOBACCO_OPTIONS } from '../../../../../../../shared/constants/quotes';
import {
  CLICK_ACTION,
  REMOVE_CENSUS,
  UPLOAD_CENSUS,
  VIEW_RATE_TABLE
} from '../../../../../../../shared/constants/tagManager';
import { useUser } from '../../../../../../../shared/hooks/useUser/useUser';
import type { ICensusValidationResult, IQuickCensusFileResponse } from '../../../../../../../shared/interfaces/quotes';
import TagManagerService from '../../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_QUOTES } from '../../../../../../../shared/translations/quotes';
import type { AgeInputMode, TobaccoStatus } from '../../../../../../../shared/types/quotes';
import { formatCurrency } from '../../../../../../../shared/utils/currency/currency';
import {
  addonIsAgeDependent,
  addonOptionLabel,
  addonRequiresTobacco,
  ageBandLabel,
  getAddonAgeRange,
  getAddonRate,
  type IGroupSelection,
  type IProductGroup
} from '../../../../../../../shared/utils/quotes/quotes';
import CensusUploadModal from '../CensusUploadModal/CensusUploadModal';
import RateTableModal from '../RateTableModal/RateTableModal';
import './ProductRow.scss';

interface IProductRowProps {
  group: IProductGroup;
  selection: IGroupSelection;
  age: number;
  employeesSize: number;
  averageAge: string;
  averageAgeError?: string | null;
  onToggle: () => void;
  onSelectAddon: (addonId: number) => void;
  onTobacco: (status: TobaccoStatus) => void;
  onAverageAgeChange: (value: string) => void;
  onAgeInputModeChange: (mode: AgeInputMode) => void;
  onCensusApply: (
    avgAge: number,
    summary: ICensusValidationResult['summary'],
    fileObj?: IQuickCensusFileResponse,
    fileName?: string,
    rowCount?: number
  ) => void;
  onCensusRemove: () => void;
  onUpdateHeadcount?: (next: number) => void;
  productTotal?: number;
  censusFile?: IQuickCensusFileResponse | null;
}

const getPlanLabelKey = (group: IProductGroup): string => {
  if (group.requiresTobacco) return TRANSLATIONS_QUOTES.create.row.coveragePlan;
  if (group.employerPaid) return TRANSLATIONS_QUOTES.create.row.tier;
  return TRANSLATIONS_QUOTES.create.row.coverage;
};

const buildRowClassName = (selected: boolean): string =>
  ['quote-product-row', selected ? 'quote-product-row--selected' : ''].filter(Boolean).join(' ');

const getDisplayCensusInfo = (
  localCensusInfo: ICensusInfo | null,
  isCensusMode: boolean,
  censusFile: IQuickCensusFileResponse | null | undefined,
  employeesSize: number,
  fallbackLabel: string
): ICensusInfo | null => {
  if (localCensusInfo) return localCensusInfo;
  if (!isCensusMode || !censusFile) return null;
  return {
    fileName: censusFile.fileName || fallbackLabel,
    rowCount: censusFile.rowCount ?? employeesSize
  };
};

const ProductRow = ({
  group,
  selection,
  age,
  employeesSize,
  averageAge,
  averageAgeError,
  onToggle,
  onSelectAddon,
  onTobacco,
  onAverageAgeChange,
  onAgeInputModeChange,
  onCensusApply,
  onCensusRemove,
  onUpdateHeadcount,
  productTotal,
  censusFile
}: IProductRowProps) => {
  const { t } = useTranslation();
  const { brokerId } = useUser();
  const [showRates, setShowRates] = useState(false);
  const [showCensusModal, setShowCensusModal] = useState(false);
  const [localCensusInfo, setLocalCensusInfo] = useState<ICensusInfo | null>(null);

  const selectedAddon = group.addons.find((addon) => addon.id === selection.addonId) ?? null;
  const showTobaccoSelect = addonRequiresTobacco(selectedAddon);
  const rate = getAddonRate(selectedAddon, age, selection.tobaccoStatus);
  const commissionPct = Math.round((selectedAddon?.commission ?? 0) * 100);
  const bandLabel = ageBandLabel(age, selectedAddon);
  const addonAgeRange = getAddonAgeRange(selectedAddon);
  const planLabelKey = getPlanLabelKey(group);

  const showPlanSelect = group.addons.length > 1;
  const showAgeInput = group.employerPaid && addonIsAgeDependent(selectedAddon);
  const hasBody = selection.selected && (showPlanSelect || showTobaccoSelect || showAgeInput);
  const showInlineRate = selection.selected && group.employerPaid && !showAgeInput && rate != null;
  const isCensusMode = selection.ageInputMode === 'census';
  const showBottomRate = selection.selected && showAgeInput && (isCensusMode ? productTotal != null : rate != null);

  const rowClassName = buildRowClassName(selection.selected);

  const requiredLabel = (text: string) => (
    <>
      {text} <span className="quote-product-row__req">*</span>
    </>
  );

  const ratePerEmployee =
    rate == null ? null : (
      <span className="quote-product-row__rate">
        {formatCurrency(rate)} {t(TRANSLATIONS_QUOTES.create.row.perEmployee)}
      </span>
    );

  const planSelect = (required: boolean) =>
    showPlanSelect && (
      <CustomSelect
        label={required ? requiredLabel(t(planLabelKey)) : t(planLabelKey)}
        value={selection.addonId == null ? '' : String(selection.addonId)}
        onChange={(event) => onSelectAddon(Number(event.target.value))}
        options={group.addons.map((addon) => ({
          value: String(addon.id),
          label: addonOptionLabel(group.name, addon.name)
        }))}
        containerClassName="quote-product-row__field"
      />
    );

  const handleCensusApply = (
    avgAge: number,
    summary: ICensusValidationResult['summary'],
    fileName: string,
    rowCount: number,
    fileObj?: IQuickCensusFileResponse
  ) => {
    onAgeInputModeChange('census');
    onCensusApply(avgAge, summary, fileObj, fileName, rowCount);
    setLocalCensusInfo({ fileName, rowCount });
    setShowCensusModal(false);
  };

  const handleRemoveCensus = () => {
    setLocalCensusInfo(null);
    onCensusRemove();
    TagManagerService.pushEvent(CLICK_ACTION, REMOVE_CENSUS);
  };

  const handleOpenCensusModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, UPLOAD_CENSUS);
    setShowCensusModal(true);
  };

  const displayCensusInfo = getDisplayCensusInfo(
    localCensusInfo,
    isCensusMode,
    censusFile,
    employeesSize,
    t(TRANSLATIONS_QUOTES.create.row.censusLoaded)
  );

  return (
    <div className={rowClassName}>
      <div className="quote-product-row__head">
        <CustomCheckbox checked={selection.selected} onChange={onToggle} label={group.name} />
        <div className="quote-product-row__head-end">
          {selection.selected && !group.employerPaid && (
            <>
              <span className="quote-product-row__commission">
                {t(TRANSLATIONS_QUOTES.create.row.commissionIncluded)} <strong>{commissionPct}%</strong>
              </span>
              <CustomButton
                variant="default"
                size="xs"
                className="quote-product-row__rates-btn"
                onClick={() => {
                  TagManagerService.pushEvent(CLICK_ACTION, VIEW_RATE_TABLE, { product: group.name });
                  setShowRates(true);
                }}
              >
                {t(TRANSLATIONS_QUOTES.create.row.viewRates)}
              </CustomButton>
            </>
          )}
          {showInlineRate && ratePerEmployee}
        </div>
      </div>

      {hasBody && (
        <div className="quote-product-row__body">
          {showAgeInput ? (
            <>
              {(showPlanSelect || showTobaccoSelect) && (
                <div className="quote-product-row__panel">
                  <div className="quote-product-row__fields">
                    {planSelect(true)}
                    {showTobaccoSelect && (
                      <CustomSelect
                        label={requiredLabel(t(TRANSLATIONS_QUOTES.create.row.tobacco))}
                        value={selection.tobaccoStatus}
                        onChange={(event) => onTobacco(event.target.value as TobaccoStatus)}
                        options={TOBACCO_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
                        containerClassName="quote-product-row__field"
                      />
                    )}
                  </div>
                </div>
              )}

              <ProductRowAgeSection
                selection={selection}
                addonAgeRange={addonAgeRange}
                averageAge={averageAge}
                averageAgeError={averageAgeError}
                onAverageAgeChange={onAverageAgeChange}
                onAgeInputModeChange={onAgeInputModeChange}
                bandLabel={bandLabel}
                censusInfo={displayCensusInfo}
                onRemoveCensus={handleRemoveCensus}
                onOpenCensusModal={handleOpenCensusModal}
              />

              {showBottomRate && (
                <div className="quote-product-row__bottom-rate">
                  <span className="quote-product-row__rate-label">
                    {isCensusMode
                      ? t(TRANSLATIONS_QUOTES.create.row.monthlyTotal)
                      : t(TRANSLATIONS_QUOTES.create.row.rate)}
                  </span>
                  {isCensusMode && productTotal != null ? (
                    <span className="quote-product-row__rate">{formatCurrency(productTotal)}</span>
                  ) : (
                    ratePerEmployee
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="quote-product-row__fields">{planSelect(false)}</div>
          )}
        </div>
      )}

      {showRates && selectedAddon && (
        <RateTableModal groupName={group.name} addon={selectedAddon} onClose={() => setShowRates(false)} />
      )}

      {showCensusModal && brokerId && (
        <CensusUploadModal
          open={showCensusModal}
          brokerId={brokerId}
          employeesSize={employeesSize}
          productName={group.name}
          addon={selectedAddon}
          tobaccoStatus={selection.tobaccoStatus}
          onClose={() => setShowCensusModal(false)}
          onApply={handleCensusApply}
          onUpdateHeadcount={onUpdateHeadcount}
        />
      )}
    </div>
  );
};

export default ProductRow;
